import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import facultyService from "../../services/facultyService";
import universityService from "../../services/universityService";
import campusService from "../../services/campusService";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const FacultyList = ({ onEdit, onView, onDelete, refreshTrigger }) => {
  const [faculties, setFaculties] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    isLastPage: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    universityId: "",
    campusId: "",
  });
  const [searchTimeout, setSearchTimeout] = useState(null);

  const { showSuccess } = useNotification();

  useEffect(() => {
    fetchUniversities();
    fetchFaculties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, refreshTrigger]);

  // Load campuses when university is selected
  useEffect(() => {
    if (filters.universityId) {
      fetchCampusesByUniversity(filters.universityId);
    } else {
      setCampuses([]);
      setFilters((prev) => ({ ...prev, campusId: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.universityId]);

  useEffect(() => {
    // Debounced search and filter
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchFaculties(0); // Reset to first page when filtering
    }, 500);

    setSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.universityId, filters.campusId]);

  const fetchUniversities = async () => {
    try {
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        setUniversities(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch universities:", error);
    }
  };

  const fetchCampusesByUniversity = async (universityId) => {
    try {
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        setCampuses(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch campuses:", error);
      setCampuses([]);
    }
  };

  const fetchFaculties = async (page = pagination.currentPage) => {
    try {
      setLoading(true);
      const response = await facultyService.getFaculties(
        page,
        pagination.pageSize,
        filters.search,
        filters.campusId || null
      );

      if (response.success) {
        setFaculties(response.data.data);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          pageSize: response.data.pageSize,
          isLastPage: response.data.isLastPage,
        });
      }
    } catch (error) {
      console.error("Failed to fetch faculties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(newSize),
      currentPage: 0,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      universityId: "",
      campusId: "",
    });
  };

  const handleDelete = async (faculty) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${faculty.nameEn}" faculty?`
      )
    ) {
      try {
        const response = await facultyService.deleteFaculty(faculty.id);
        if (response.success) {
          showSuccess("Faculty deleted successfully");
          fetchFaculties();
        }
      } catch (error) {
        console.error("Failed to delete faculty:", error);
      }
    }
  };

  const handleToggleStatus = async (faculty) => {
    const newStatus = !faculty.status;
    const action = newStatus ? "activate" : "deactivate";

    if (
      window.confirm(`Are you sure you want to ${action} "${faculty.nameEn}"?`)
    ) {
      try {
        const updateData = {
          campusId: faculty.campusId,
          nameEn: faculty.nameEn,
          nameLocal: faculty.nameLocal,
          status: newStatus,
        };

        const response = await facultyService.updateFaculty(
          faculty.id,
          updateData
        );
        if (response.success) {
          showSuccess(`Faculty ${action}d successfully`);
          fetchFaculties();
        }
      } catch (error) {
        console.error("Failed to toggle status:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(
      (value) => value && value.toString().trim()
    ).length;
  };

  return (
    <div className="faculty-list">
      <ListToolbar
        title="Faculties"
        subtitle={`${pagination.totalElements} facult${pagination.totalElements !== 1 ? "ies" : "y"} found`}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label small fw-medium">Search</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search faculties..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label small fw-medium">University</label>
            <select
              className="form-select"
              value={filters.universityId}
              onChange={(e) =>
                handleFilterChange("universityId", e.target.value)
              }
            >
              <option value="">All Universities</option>
              {universities.map((university) => (
                <option key={university.id} value={university.id}>
                  {university.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label small fw-medium">Campus</label>
            <select
              className="form-select"
              value={filters.campusId}
              onChange={(e) => handleFilterChange("campusId", e.target.value)}
              disabled={!filters.universityId}
            >
              <option value="">
                {!filters.universityId ? "Select University First" : "All Campuses"}
              </option>
              {campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>
          </div>

          {getActiveFiltersCount() > 0 && (
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                Clear All
              </button>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Faculties Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading faculties...</p>
            </div>
          ) : faculties.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-graduation-cap text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Faculties Found</h5>
              <p className="text-muted">
                {getActiveFiltersCount() > 0
                  ? "No faculties match your current filters"
                  : "No faculties available"}
              </p>
              {getActiveFiltersCount() > 0 && (
                <button
                  className="btn btn-outline-primary"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Faculty</th>
                    <th>Campus</th>
                    <th>Status</th>
                    <th>Statistics</th>
                    <th>Created</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {faculties.map((faculty, index) => (
                    <tr key={faculty.id}>
                      <td className="align-middle">
                        {pagination.currentPage * pagination.pageSize +
                          index +
                          1}
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          <div
                            className={`faculty-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                              faculty.status ? "bg-success" : "bg-secondary"
                            }`}
                            style={{ width: "40px", height: "40px" }}
                          >
                            <i className="fas fa-graduation-cap text-white"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {faculty.nameEn}
                            </div>
                            <small className="text-muted">
                              {faculty.nameLocal}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className="fw-medium text-primary">
                          {faculty.campusName}
                        </div>
                        <small className="text-muted">
                          Campus ID: {faculty.campusId}
                        </small>
                      </td>
                      <td className="align-middle">
                        <span
                          className={`badge ${
                            faculty.status ? "bg-success" : "bg-danger"
                          }`}
                        >
                          <i
                            className={`fas ${
                              faculty.status
                                ? "fa-check-circle"
                                : "fa-times-circle"
                            } me-1`}
                          ></i>
                          {faculty.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex gap-3">
                          <div className="text-center">
                            <div className="fw-bold text-primary">
                              {faculty.studentCount || 0}
                            </div>
                            <small className="text-muted">Students</small>
                          </div>
                          <div className="text-center">
                            <div className="fw-bold text-info">
                              {faculty.departmentCount || 0}
                            </div>
                            <small className="text-muted">Departments</small>
                          </div>
                        </div>
                      </td>
                      <td className="align-middle">
                        <small className="text-muted">
                          {formatDate(faculty.createdAt)}
                        </small>
                      </td>
                      <td className="align-middle text-center">
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => onView(faculty)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => onEdit(faculty)}
                            title="Edit Faculty"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className={`btn btn-sm ${
                              faculty.status
                                ? "btn-outline-warning"
                                : "btn-outline-success"
                            }`}
                            onClick={() => handleToggleStatus(faculty)}
                            title={faculty.status ? "Deactivate" : "Activate"}
                          >
                            <i
                              className={`fas ${
                                faculty.status ? "fa-pause" : "fa-play"
                              }`}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(faculty)}
                            title="Delete Faculty"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>

        {/* Pagination */}
        {!loading && faculties.length > 0 && (
          <div className="card-footer bg-light border-0">
            <div className="row align-items-center">
              <div className="col-md-6">
                <span className="text-muted small">
                  Showing {pagination.currentPage * pagination.pageSize + 1} to{" "}
                  {Math.min(
                    (pagination.currentPage + 1) * pagination.pageSize,
                    pagination.totalElements
                  )}{" "}
                  of {pagination.totalElements} entries
                </span>
              </div>
              <div className="col-md-6">
                <nav>
                  <ul className="pagination pagination-sm justify-content-end mb-0">
                    <li
                      className={`page-item ${
                        pagination.currentPage === 0 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage - 1)
                        }
                        disabled={pagination.currentPage === 0}
                      >
                        Previous
                      </button>
                    </li>

                    {/* Page numbers */}
                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i;
                        } else if (pagination.currentPage < 3) {
                          pageNum = i;
                        } else if (
                          pagination.currentPage >=
                          pagination.totalPages - 2
                        ) {
                          pageNum = pagination.totalPages - 5 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }

                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              pagination.currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum + 1}
                            </button>
                          </li>
                        );
                      }
                    )}

                    <li
                      className={`page-item ${
                        pagination.isLastPage ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage + 1)
                        }
                        disabled={pagination.isLastPage}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyList;
